import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Pagination,
  message,
  Form,
  Input,
  Avatar,
  Space,
  Skeleton,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ReusableTable from "../../components/ReusableTable";
import api from "../../utils/api";// Your axios instance

const GuardList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);

  // Fetch users
  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get("/users", {
        params: { page, per_page: pageSize },
      });
      setUsers(response.data.data || response.data);
      setTotal(response.data.total || response.data.length);
      setCurrentPage(page);
    } catch (error) {
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // View User
  const handleView = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  // Edit User
  const handleEdit = (user) => {
    setSelectedUser(user);
    setPreviewImage(user.document || null);
    setFileToUpload(null);

    form.setFieldsValue({
      name: user.name,
      email: user.email,
      phone_number: user.phone_number,
      notes: user.notes || "",
    });

    setIsEditModalOpen(true);
  };

  // Delete User
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete User?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await api.delete(`/users/${id}`);
          message.success("User deleted successfully");
          fetchUsers(currentPage);
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to delete user");
        }
      },
    });
  };

  // Handle Image Change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("File must be under 5MB");
      return;
    }

    setFileToUpload(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  };

  // Update User
  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("phone_number", values.phone_number);
      if (values.notes) formData.append("notes", values.notes);
      if (fileToUpload) formData.append("document", fileToUpload);
      formData.append("_method", "PUT"); // Laravel-style PUT via POST

      await api.post(`/users/${selectedUser.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success("User updated successfully");
      setIsEditModalOpen(false);
      fetchUsers(currentPage);
    } catch (error) {
      if (error.errorFields) return;
      const msg = error.response?.data?.message || "Failed to update user";
      message.error(msg);
    }
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    setPreviewImage(null);
    setFileToUpload(null);
    form.resetFields();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.document || "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"}
            icon={<UserOutlined />}
            size={40}
          />
          <span className="font-medium">{text}</span>
        </Space>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 260,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" type="primary" />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} danger size="small" />
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)} size="small" />
          <Button
            icon={<MailOutlined />}
            onClick={() => navigate("/dashboard/notices", { state: { selectedUser: record } })}
            size="small"
          >
            Notice
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full bg-slate-200 p-6 pb-20 rounded-lg shadow-md">
      <div className="bg-white rounded-lg p-6">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Guard Users List</h1>
          <Link to="/dashboard/add-guard">
            <Button type="primary" icon={<PlusOutlined />} className="bg-green-600 hover:bg-green-700">
              Add Guard User
            </Button>
          </Link>
        </div>

        {loading ? (
          <Skeleton active />
        ) : (
          <>
            <ReusableTable
              columns={columns}
              data={users}
              rowKey="id"
              loading={loading}
              pagination={false}
              scroll={{ x: 800 }}
            />

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              onChange={handlePageChange}
              className="mt-6 text-center"
              showSizeChanger={false}
            />
          </>
        )}
      </div>

      {/* View Modal */}
      <Modal
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <div className="text-center">
            <img
              src={selectedUser.document || "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"}
              alt="profile"
              className="w-48 h-48 object-cover rounded-full mx-auto mb-4"
            />
            <h3 className="text-xl font-bold">{selectedUser.name}</h3>
            <p className="text-gray-600">{selectedUser.email}</p>
            <p className="mt-2">{selectedUser.phone_number}</p>
            {selectedUser.notes && <p className="mt-4 text-sm italic">"{selectedUser.notes}"</p>}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Guard User"
        open={isEditModalOpen}
        onCancel={handleCloseEdit}
        onOk={handleUpdate}
        okText="Update"
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phone_number"
            rules={[
              { required: true, message: "Phone is required" },
              { pattern: /^[0-9]{10}$/, message: "Must be 10 digits" },
            ]}
          >
            <Input maxLength={10} />
          </Form.Item>

          <Form.Item label="Notes (Optional)" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item label="Profile Image / Document">
            {previewImage && (
              <img src={previewImage} alt="preview" className="w-full h-48 object-contain mb-3 rounded" />
            )}
            <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleImageChange} />
            <p className="text-xs text-gray-500 mt-1">Max 5MB: JPG, PNG, PDF</p>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GuardList;